import { PropsWithChildren, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { isEqual } from "lodash";
import { RadioGroup } from "@headlessui/react";
import { useToast, useUpdateWorkTimeListMutation } from "@/hooks";
import { Employee, WorkTime } from "@/types";
import { WorktimeValidationSchema } from "@/shared";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib";
import { Button } from "@/components/ui/button";
import { Wand2 } from "lucide-react";
import { DisplayError } from "@/utils";

type WorkTimeData = {
  id: any;
  day: string;
  start_time: string;
  end_time: string;
  start_interval: string;
  end_interval: string;
};

type FormWorkTimesCollaboratorProps = {
  handleClose: () => void;
  collaborator: Employee;
  refetch?: () => void;
};

export function FormWorkTimesCollaborator({
  collaborator,
  refetch,
}: FormWorkTimesCollaboratorProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isLoadingGenerateRandomWorkTimes, setIsLoadingGenerateRandomWorkTimes] = useState(false);
  const [day, setDay] = useState<string>("sunday");
  const { mutateAsync, isLoading } = useUpdateWorkTimeListMutation();
  const [workTimes, setWorkTimes] = useState<WorkTime[]>([]);
  const [tempWorkTimes, setTempWorkTimes] = useState<
    Record<string, WorkTimeData>
  >({});

  const getDayData = (selectedDay: string): WorkTimeData | undefined => {
    if (tempWorkTimes[selectedDay]) {
      return tempWorkTimes[selectedDay];
    }

    const originalData = collaborator.work_times.find(
      (wt) => wt.day === selectedDay
    );
    if (originalData) {
      return {
        id: originalData.id,
        day: originalData.day,
        start_time: originalData.start_time,
        end_time: originalData.end_time,
        start_interval: originalData.start_interval,
        end_interval: originalData.end_interval,
      };
    }
  };

  const initialValues = {
    id: getDayData(day)?.id || "",
    day: getDayData(day)?.day || "",
    start_time: formatTime(getDayData(day)?.start_time) || "",
    end_time: formatTime(getDayData(day)?.end_time) || "",
    start_interval: formatTime(getDayData(day)?.start_interval) || "",
    end_interval: formatTime(getDayData(day)?.end_interval) || "",
  };

  const formik = useFormik({
    initialValues,
    validationSchema: WorktimeValidationSchema(),
    onSubmit: async () => {
      updateWorkTime();
    },
  });

  useEffect(() => {
    const currentDayData = getDayData(day);

    if (currentDayData) {
      formik.setValues({
        id: currentDayData.id || "",
        day: currentDayData.day || "",
        start_time: formatTime(currentDayData.start_time) || "",
        end_time: formatTime(currentDayData.end_time) || "",
        start_interval: formatTime(currentDayData.start_interval) || "",
        end_interval: formatTime(currentDayData.end_interval) || "",
      });
    }
  }, [day,isLoadingGenerateRandomWorkTimes]);

  async function updateWorkTime() {
    try {
      await mutateAsync(
        {
          employeesId: collaborator.id,
          work_times: workTimes,
        },
        {
          onSuccess: ({ data }) => {
            toast({
              title: t("LABEL.success"),
              description: t("MESSAGE.updated successfully"),
            });
            if (refetch) {
              refetch();
            }
            data.map((item) => {
              save(item);
            });
          },
          onError: (error) => {
            toast({
              title: t("LABEL.error"),
              description: DisplayError(error, t("MESSAGE.error_message")),
              variant: "destructive",
            });
          },
        }
      );
    } catch (error) {
      toast({
        title: t("LABEL.error"),
        description: DisplayError(error, t("MESSAGE.error_message")),
        variant: "destructive",
      });
    }
  }

  const isObjectEmpty = (obj: any) => {
    return !Object.keys(obj || {}).length;
  };

   
  function save(workTime: Partial<WorkTimeData>) {
    const completeWorkTime: WorkTimeData = {
      id: workTime.id || getDayData(day)?.id || "",
      day: workTime.day || getDayData(day)?.day || "",
      start_time:
        formatTime(workTime.start_time) || formik.values.start_time || "",
      end_time: formatTime(workTime.end_time) || formik.values.end_time || "",
      start_interval:
        formatTime(workTime.start_interval) || formik.values.start_interval,
      end_interval:
        formatTime(workTime.end_interval) || formik.values.end_interval,
    };

    setTempWorkTimes((prev) => ({
      ...prev,
      [completeWorkTime.day]: completeWorkTime,
    }));

    const index = workTimes.findIndex(
      (item) => item.id === completeWorkTime.id
    );
    if (index !== -1) {
      const existingWorkTime = workTimes[index];
      if (
        formatTime(existingWorkTime.start_time) !==
          completeWorkTime.start_time ||
        formatTime(existingWorkTime.end_time) !== completeWorkTime.end_time ||
        formatTime(existingWorkTime.start_interval) !==
          completeWorkTime.start_interval ||
        formatTime(existingWorkTime.end_interval) !==
          completeWorkTime.end_interval
      ) {
        const updatedWorkTimes = [...workTimes];
        updatedWorkTimes[index] = {
          ...updatedWorkTimes[index],
          ...completeWorkTime,
        };
        setWorkTimes(updatedWorkTimes);
      }
    } else {
      const isDuplicate = workTimes.some((item) =>
        isEqual(item, completeWorkTime)
      );
      if (!isDuplicate) {
        setWorkTimes([
          ...workTimes,
          {
            ...completeWorkTime,
            availability: "",
          },
        ]);
      }
    }
  }

   
  function getDay(): WorkTimeData | undefined {
    const data = collaborator.work_times.find(
      (workTime) => workTime.day === day
    );
    return data;
  }

  function formatTime(time?: string | null): string {
    if (!time) return "";

    if (/^\d{2}:\d{2}$/.test(time)) {
      return time;
    }

    if (/^\d{2}:\d{2}:\d{2}$/.test(time)) {
      return time.slice(0, -3);
    }

    return time;
  }

  function generateRandomWorkTimes(): WorkTimeData[] {
    setIsLoadingGenerateRandomWorkTimes(true);

    const days = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];

    const startTime = "08:00";
    const startInterval = "12:00";
    const endInterval = "13:00";
    const endTime = "22:00";

    const workTimes = days.map((day) => ({
      id: getDayData(day)?.id || "",
      day,
      start_time: startTime,
      start_interval: startInterval,
      end_interval: endInterval,
      end_time: endTime,
    }));

    setIsLoadingGenerateRandomWorkTimes(false);
    return workTimes;
  }

  function handleGenerateRandom() {
    const generated = generateRandomWorkTimes();

    const newTemp: Record<string, WorkTimeData> = {};
    generated.forEach((item) => {
      newTemp[item.day] = item;
    });

    setTempWorkTimes((prev) => ({
      ...prev,
      ...newTemp,
    }));

    setWorkTimes(
      generated.map((item) => ({
        ...item,
        availability: "",
      }))
    );

    const currentDayData = newTemp[day];
    if (currentDayData) {
      formik.setValues({
        id: currentDayData.id || "",
        day: currentDayData.day || "",
        start_time: formatTime(currentDayData.start_time) || "",
        end_time: formatTime(currentDayData.end_time) || "",
        start_interval: formatTime(currentDayData.start_interval) || "",
        end_interval: formatTime(currentDayData.end_interval) || "",
      });
    }

    toast({
      title: t("LABEL.success"),
      description: t("Horários aleatórios gerados com sucesso."),
    });
  }



  return (
    <form onSubmit={formik.handleSubmit} className="p-4">
      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="secondary" onClick={handleGenerateRandom} isLoading={isLoadingGenerateRandomWorkTimes} disabled={isLoadingGenerateRandomWorkTimes}>
          <Wand2 size={14} />
          <span>{t("LABEL.automatically generate")}</span>
        </Button>
      </div>
      <div className="space-y-5">
        <RadioGroup
          className="workTimeItem flex items-center justify-center gap-5 my-8"
          value={day}
          onChange={(newDay) => {
            setDay(newDay);
            const newWorkTime = getDayData(newDay);
            formik.setValues({
              id: newWorkTime?.id || newDay,
              day: newDay,
              start_time: formatTime(newWorkTime?.start_time) || "",
              end_time: formatTime(newWorkTime?.end_time) || "",
              start_interval:
                formatTime(newWorkTime?.start_interval) ||
                formik.values.start_interval,
              end_interval:
                formatTime(newWorkTime?.end_interval) ||
                formik.values.end_interval,
            });
          }}
        >
          <RadioGroup.Option value="sunday">
            {({ checked }) => (
              <DayWeek checked={checked}>{t("LABEL.sunday")}</DayWeek>
            )}
          </RadioGroup.Option>

          <RadioGroup.Option value="monday">
            {({ checked }) => (
              <DayWeek checked={checked}>{t("LABEL.monday")}</DayWeek>
            )}
          </RadioGroup.Option>

          <RadioGroup.Option value="tuesday">
            {({ checked }) => (
              <DayWeek checked={checked}>{t("LABEL.tuesday")}</DayWeek>
            )}
          </RadioGroup.Option>

          <RadioGroup.Option value="wednesday">
            {({ checked }) => (
              <DayWeek checked={checked}>{t("LABEL.wednesday")}</DayWeek>
            )}
          </RadioGroup.Option>

          <RadioGroup.Option value="thursday">
            {({ checked }) => (
              <DayWeek checked={checked}>{t("LABEL.thursday")}</DayWeek>
            )}
          </RadioGroup.Option>

          <RadioGroup.Option value="friday">
            {({ checked }) => (
              <DayWeek checked={checked}>{t("LABEL.friday")}</DayWeek>
            )}
          </RadioGroup.Option>

          <RadioGroup.Option value="saturday">
            {({ checked }) => (
              <DayWeek checked={checked}>{t("LABEL.saturday")}</DayWeek>
            )}
          </RadioGroup.Option>
        </RadioGroup>

        {!isObjectEmpty(getDay()) && getDay() && (
          <>
            <div className="space-y-3">
              <strong className="text-app-fg-strong font-semibold">
                {t("LABEL.worktimes")}
              </strong>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-3">
                  <Label
                    className="text-base font-medium text-app-fg"
                    htmlFor="start_time"
                  >
                    {t("LABEL.start")}:{" "}
                    <strong className="text-app-danger">*</strong>
                  </Label>
                  <Input
                    type="time"
                    className="px-5 py-1 border border-app-border focus:border-brand"
                    onChange={(e) => {
                      formik.setFieldTouched("start_time", true);
                      formik.setFieldValue("start_time", e.target.value);
                      save({
                        ...workTimes,
                        start_time: formatTime(e.target.value),
                      });
                    }}
                    onBlur={() => formik.setFieldTouched("start_time", true)}
                    value={formik.values.start_time as string}
                    isInvalid={
                      !!(formik.touched.start_time && formik.errors.start_time)
                    }
                    isValid={
                      formik.touched.start_time && !formik.errors.start_time
                    }
                    step="60"
                  />
                </div>

                <div className="flex flex-col gap-3">
                  <Label
                    className="text-base font-medium text-app-fg"
                    htmlFor="end_time"
                  >
                    {t("LABEL.end")}:{" "}
                    <strong className="text-app-danger">*</strong>
                  </Label>
                  <Input
                    type="time"
                    className="px-5 py-1 border border-app-border focus:border-brand"
                    onChange={(e) => {
                      formik.setFieldTouched("end_time", true);
                      formik.setFieldValue("end_time", e.target.value);
                      save({
                        ...workTimes,
                        end_time: formatTime(e.target.value),
                      });
                    }}
                    onBlur={() => formik.setFieldTouched("end_time", true)}
                    value={formik.values.end_time as string}
                    isInvalid={
                      !!(formik.touched.end_time && formik.errors.end_time)
                    }
                    isValid={formik.touched.end_time && !formik.errors.end_time}
                    step="60"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <strong className="text-app-fg-strong font-semibold">
                {t("LABEL.lunchtime")}
              </strong>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-3">
                  <Label
                    className="text-base font-medium text-app-fg"
                    htmlFor="start_interval"
                  >
                    {t("LABEL.start")}:{" "}
                    <strong className="text-app-danger">*</strong>
                  </Label>
                  <Input
                    type="time"
                    className="px-5 py-1 border border-app-border focus:border-brand"
                    onChange={(e) => {
                      formik.setFieldTouched("start_interval", true);
                      formik.setFieldValue("start_interval", e.target.value);
                      save({
                        ...workTimes,
                        start_interval: formatTime(e.target.value),
                      });
                    }}
                    onBlur={() =>
                      formik.setFieldTouched("start_interval", true)
                    }
                    value={formik.values.start_interval as string}
                    isInvalid={
                      !!(
                        formik.touched.start_interval &&
                        formik.errors.start_interval
                      )
                    }
                    isValid={
                      !!(
                        formik.touched.start_interval &&
                        !formik.errors.start_interval
                      )
                    }
                    step="60"
                  />
                </div>
                <div className="flex flex-col gap-3">
                  <Label
                    className="text-base font-medium text-app-fg"
                    htmlFor="end_interval"
                  >
                    {t("LABEL.end")}:{" "}
                    <strong className="text-app-danger">*</strong>
                  </Label>
                  <Input
                    type="time"
                    className="px-5 py-1 border border-app-border focus:border-brand"
                    onChange={(e) => {
                      formik.setFieldValue("end_interval", e.target.value);
                      save({
                        ...workTimes,
                        end_interval: formatTime(e.target.value),
                      });
                    }}
                    onBlur={() => formik.setFieldTouched("end_interval", true)}
                    value={formik.values.end_interval as string}
                    isInvalid={
                      !!(
                        formik.touched.end_interval &&
                        formik.errors.end_interval
                      )
                    }
                    isValid={
                      !!(
                        formik.touched.end_interval &&
                        !formik.errors.end_interval
                      )
                    }
                    step="60"
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <div className="flex items-center justify-center gap-2 mt-5">
        <Button className="px-10" isLoading={isLoading} disabled={isLoading}>
          Salvar
        </Button>
      </div>
    </form>
  );
}

type DayWeekProps = PropsWithChildren & {
  checked?: boolean;
  className?: string;
};

export function DayWeek({ checked, className, children }: DayWeekProps) {
  return (
    <div
      data-checked={checked ? true : false}
      className={cn(
        "w-max flex items-center gap-2.5 px-3 py-2 rounded-md cursor-pointer border border-dashed bg-app-surface-muted border-app-border data-[checked=true]:bg-brand data-[checked=true]:border-brand-hover",
        className
      )}
    >
      <div
        data-checked={checked ? true : false}
        className="w-4 h-4 bg-app-surface border-[2px] border-app-border data-[checked=true]:border-brand-hover rounded-full"
      />
      <span
        data-checked={checked ? true : false}
        className="text-sm font-medium text-app-fg data-[checked=true]:text-brand-fg"
      >
        {children}
      </span>
    </div>
  );
}
